/**
 * The in-season half of DEC-99 (`docs/adr/9039`, generalising ADR 6011): which runs a page of each
 * kind may open on by itself, and the «Scegli un run» gate that opens the newest instead of asking.
 *
 * **"Eligible" is each page's own list, never a new rule.** The run directory publishes no run
 * kind (F4) and no status: it lists recorded runs only, so a run in flight or one that failed
 * before recording is not in it. The kind is told apart exactly as the page already tells it:
 *
 *   - `price` — ADR 6011's rule, `offeredRuns(…, 'price')` in `CycleRunPicker`: the directory
 *     newest recorded first, keeping a run that holds a price step (`price_actions` on its row);
 *   - `distribute` / `rebalance` — `useKindRuns` (ADR 9033 §7): of the 24 newest runs, those whose
 *     row counts a proposal / transfer line (`proposal_lines` / `transfer_lines`, ADR 6176 — read
 *     off the run directory the page already holds; since 2026-10-13 no queue is probed, where it
 *     was up to 24 requests per page, and never the run digest, which rolls the whole book up and
 *     kept these pages from ever choosing on production; ADR 9039, amendment 2026-10-02); a run
 *     a newer tick has superseded for the kind is not offered either (its row names the run that
 *     took the lines over: audit 2, 2026-10-12 — the rebalancing page opened on a run its own
 *     banner called superseded, under «scelta automaticamente: la più recente»);
 *   - `pack` — the execution pack (amendment 2026-10-02): of the 24 newest runs, those whose
 *     write-back holds a command (`usePackRuns`: one grouped count per run, `limit=1` — never the
 *     pack itself, which is unbounded). The newest run is usually a fresh tick whose commands are
 *     not written yet; opening on it showed an empty pack while 7,019 approved commands sat on the
 *     run before it (signed-in production check, 2026-10-02);
 *   - `any` — the directory, newest recorded first (`newestFirst`): the backlog, cycle and
 *     provenance pages read what any run left.
 *
 * The eligibility reads are issued only while the URL names no run (or a page's «Scegli un run»
 * draws), so a page opened on a run pays nothing for this.
 */

import { inseasonListRuns, listWriteBackGroups } from '@/api/generated';
import type { CycleRunSummary } from '@/api/generated';
import { useApiCollection, useApiQueries } from '@/api';
import { ChooseRunState, useRunDefault, useRunWordsOf, type RunScope, type UseRunDefault } from '@/planning';
import { Skeleton } from '@/primitives';

import { KIND_PROBE_RUNS, isSupersededFor, newestFirst, useKindRuns, type InSeasonRunKind } from './choose-run';
import { navigateToScope, replaceScope, RUN_ID_PARAM } from './scope';

/** What a page's run must be to open by itself: one kind, a run with write-back commands, or any run. */
export type InSeasonRunScope = InSeasonRunKind | 'pack' | 'any';

/** A directory row, with its price-step count when the picker asked for one. */
export interface PickerRun {
  readonly row: CycleRunSummary;
  /** Price steps the run holds (its row's `price_actions`); `null` when not asked. */
  readonly steps: number | null;
}

/** When a run was recorded: `recorded_at` (ADR 6034), else `generated_at`. */
function recordedAt(row: { readonly generated_at: string; readonly recorded_at?: string | null | undefined }): string {
  return row.recorded_at ?? row.generated_at;
}

/**
 * The runs to offer, newest first. The directory is already newest first — by `recorded_at`
 * since ADR 6034; by `generated_at`, the monthly window's end, before it, which tied every tick
 * of a month (bata-ui#145) — and the sort is stable, so runs stamped with the same instant keep
 * the platform's order. With `price`, a run whose count is zero is dropped — it is not
 * a price run, or a price run that proposed nothing, and either way there is nothing on this
 * screen to read for it. ADR 6011's eligibility: the first of these, with `price`.
 */
export function offeredRuns(runs: readonly PickerRun[], purpose: 'price' | 'any'): readonly PickerRun[] {
  const ordered = [...runs].sort((a, b) => {
    const left = recordedAt(a.row);
    const right = recordedAt(b.row);
    return left < right ? 1 : left > right ? -1 : 0;
  });
  return purpose === 'price' ? ordered.filter((run) => run.steps === null || run.steps > 0) : ordered;
}

/**
 * The directory with each run's price-step count — read off the row (`price_actions`, ADR 6176),
 * only when `counted` is set. No request but the directory's own, the one `CycleRunPicker` shares.
 */
export function usePricedRuns(counted: boolean): {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  readonly data: readonly PickerRun[] | undefined;
} {
  // No `country_key` filter: `ScopedRows` rewrites this read to the principal's geography
  // server-side (G3.1), so a filter here could only narrow what a planner may see.
  const runs = useApiCollection(inseasonListRuns, { query: {} });
  return {
    isPending: runs.isPending,
    isError: runs.isError,
    error: runs.error,
    data: runs.data?.map((row): PickerRun => ({ row, steps: counted ? row.price_actions : null })),
  };
}

/**
 * The runs whose write-back holds at least one command, newest first — the execution pack's
 * eligibility (ADR 9039, amendment 2026-10-02). One bounded read per run among the
 * {@link KIND_PROBE_RUNS} newest: `GET /inseason/write-back/groups?group_by=subject_kind&run_id=
 * &country_key=&limit=1`, whose `totals.commands` counts every command of the run (the platform's
 * GROUP BY, never the pack's lines). A run whose count could not be read is not offered, as
 * `useKindRuns` does: its commands are unknown. `undefined` while the counts are read; `[]` when
 * the directory failed. Nothing is read while `enabled` is false.
 */
export function usePackRuns(enabled: boolean): readonly string[] | undefined {
  const directory = useApiCollection(inseasonListRuns, { query: {} });
  const probed = newestFirst(directory.data ?? []).slice(0, KIND_PROBE_RUNS);
  const counts = useApiQueries(
    listWriteBackGroups,
    enabled
      ? probed.map((row) => ({ query: { group_by: 'subject_kind' as const, country_key: row.country_key, run_id: row.run_id, limit: 1 } }))
      : [],
  );
  if (!enabled) return [];
  if (directory.isError) return [];
  if (directory.data === undefined || counts.some((count) => count.isPending)) return undefined;
  return probed.flatMap((row, index) => ((counts[index]?.data?.totals.commands ?? 0) > 0 ? [row.run_id] : []));
}

/**
 * The run ids a page of `scope` may open on by itself, newest first — `undefined` while they are
 * read, `[]` when there are none or the read failed (the page then asks, and says why). Nothing
 * is read while `enabled` is false.
 */
export function useEligibleRunIds(scope: InSeasonRunScope | null, enabled: boolean): readonly string[] | undefined {
  const on = enabled && scope !== null;
  const priced = usePricedRuns(on && scope === 'price');
  const kinded = useKindRuns(scope === 'distribute' || scope === 'rebalance' ? scope : 'distribute', on && (scope === 'distribute' || scope === 'rebalance'));
  const packed = usePackRuns(on && scope === 'pack');
  const directory = useApiCollection(inseasonListRuns, { query: {} });
  if (!on) return [];
  switch (scope) {
    case 'price':
      if (priced.isError) return [];
      // A price run a newer tick superseded is history, not the newest (2026-10-22; ADR 6176 names
      // it on the row, as for distribute and rebalance). `offeredRuns` still lists it in the picker.
      return priced.data === undefined ? undefined : offeredRuns(priced.data, 'price')
        .filter((run) => !isSupersededFor('price', run.row))
        .map((run) => run.row.run_id);
    case 'distribute':
    case 'rebalance':
      if (kinded.isError) return [];
      return kinded.current;
    case 'pack':
      return packed;
    case 'any':
      // No supersession here: it is per kind of line (proposals, transfers, price steps), and a run
      // of "any" kind has no field that says another run took over what it was read for.
      if (directory.isError) return [];
      return directory.data === undefined ? undefined : newestFirst(directory.data).map((row) => row.run_id);
  }
}

/** The memory scope of an in-season page: the run kind; the run names its own market. */
export function inseasonRunScope(scope: InSeasonRunScope): RunScope {
  return { kind: `inseason.${scope}` };
}

/** Write a run chosen FOR the planner: `?runId=`, replacing the history entry (ADR 6011). */
export function writeChosenRun(runId: string): void {
  const next = new URLSearchParams(window.location.search);
  next.set(RUN_ID_PARAM, runId);
  replaceScope(next);
}

/** Open a run the planner chose: `?runId=`, a history step. */
export function openRun(runId: string): void {
  const next = new URLSearchParams(window.location.search);
  next.set(RUN_ID_PARAM, runId);
  navigateToScope(next);
}

/** «Il più recente»: forget the choice and drop `?runId=`, so the page opens on the newest. */
export function openNewest(memory: UseRunDefault): void {
  memory.chooseNewest();
  const next = new URLSearchParams(window.location.search);
  next.delete(RUN_ID_PARAM);
  navigateToScope(next);
}

/** DEC-99 for an in-season page: resolve its run, and write a chosen one into the URL. */
export function useInSeasonRunDefault(scope: InSeasonRunScope | null, urlRunId: string | null): UseRunDefault {
  const offered = useEligibleRunIds(scope, urlRunId === null);
  return useRunDefault({
    scope: inseasonRunScope(scope ?? 'any'),
    urlRunId,
    offered,
    write: writeChosenRun,
    enabled: scope !== null,
  });
}

/**
 * A page with no run in its URL: the newest eligible run is opened, and
 * only when there is none is «Scegli un run» drawn over `runs` — the page's own list.
 */
export function DefaultingRunChooser({ scope, objectKey, runs }: {
  readonly scope: InSeasonRunScope;
  readonly objectKey: string;
  readonly runs: React.ComponentProps<typeof ChooseRunState>['runs'];
}): React.JSX.Element {
  const memory = useInSeasonRunDefault(scope, null);
  const source = memory.resolution.source;
  if (source === 'pending' || source === 'newest') {
    // Reading the runs, or the chosen one is being written into the URL: the page follows.
    return <div aria-busy="true"><Skeleton rows={3} /></div>;
  }
  const choose = (runId: string): void => {
    memory.choose(runId);
    openRun(runId);
  };
  return <ChooseRunState objectKey={objectKey} runs={runs} onChoose={choose} />;
}

/**
 * A page reading runs of `kind`, with no run in its URL: the newest run of that kind opens, and «Scegli un run» over the runs of that kind is drawn only when
 * there is none (ADR 9033 as amended by DEC-99). Moved here from `./choose-run.tsx`.
 */
export function ChooseInSeasonRun({ kind, objectKey }: {
  readonly kind: InSeasonRunKind;
  /** What the page shows once a run is chosen — "the proposals". */
  readonly objectKey: string;
}): React.JSX.Element {
  const runs = useKindRuns(kind);
  return <DefaultingRunChooser scope={kind} objectKey={objectKey} runs={runs} />;
}

/**
 * A run as the default notes name it — in words, never its id (audit A1, 2026-10-10): market,
 * period, channel and the day it ran; «un’esecuzione precedente» for a run the directory does not
 * list.
 */
export function useRunName(): (runId: string) => string {
  const words = useRunWordsOf();
  return (runId: string): string => words(runId);
}
